import getUserLocale from 'get-user-locale';
import type { PropsWithChildren, ReactNode } from 'react';
import { useEffect, useState } from 'react';
import { IntlProvider as ReactIntlProvider } from 'react-intl';
import type { IntlConfig } from 'react-intl/src/types';

export type SupportedLocales =
  | 'de-DE'
  | 'el-GR'
  | 'en-US'
  | 'es-ES'
  | 'fr-CA'
  | 'fr-FR'
  | 'it-IT'
  | 'ja-JP'
  | 'nl-NL'
  | 'sv-SE'
  | 'zh-CN'
  | 'zh-TW';

type Messages = {
  [key: string]: {
    type: number;
    value: string;
  }[];
};

type ProviderProps = Omit<
  PropsWithChildren<IntlConfig>,
  'defaultLocale' | 'locale' | 'messages'
> & {
  children: ReactNode;
  /**
   * Optional custom locale detection function
   *
   * When provided, this function will be used instead of the default
   * getUserLocale() from 'get-user-locale' package. This allows
   * applications to implement custom locale detection logic, such as:
   * - Reading from URL query parameters
   * - Checking session storage or cookies
   * - Implementing priority-based locale detection
   *
   * @returns The locale string (e.g., 'en-US', 'fr-FR')
   *
   * @example
   * ```tsx
   * const getCustomLocale = () => {
   *   const urlParams = new URLSearchParams(window.location.search);
   *   return urlParams.get('lang') || getUserLocale();
   * };
   *
   * <IntlProvider loadLocaleData={loadLocaleData} getUserLocale={getCustomLocale}>
   *   {children}
   * </IntlProvider>
   * ```
   */
  getUserLocale?: () => string;
  loadLocaleData: (locale: SupportedLocales) => Promise<{
    default: Messages;
  }>;
};

export const IntlProvider = ({
  children,
  loadLocaleData,
  getUserLocale: customGetUserLocale,
  ...rest
}: ProviderProps) => {
  // check if locale is supported, else fallback to 'en-US'
  const supportedLocales: SupportedLocales[] = [
    'en-US',
    'el-GR',
    'fr-CA',
    'nl-NL',
    'sv-SE',
    'zh-TW',
    'fr-FR',
    'de-DE',
    'es-ES',
    'it-IT',
    'ja-JP',
    'zh-CN',
  ];

  // Use custom locale detection if provided, otherwise use default
  const userLocale = customGetUserLocale
    ? customGetUserLocale()
    : getUserLocale();
  const isSupported = supportedLocales.includes(userLocale as SupportedLocales);
  const localeValue = isSupported ? userLocale : 'en-US';

  const [messages, setMessages] = useState<Messages>({});

  useEffect(() => {
    let isMounted = true;

    loadLocaleData(localeValue as SupportedLocales).then((messages) => {
      if (isMounted) {
        setMessages(messages.default);
      }
    });

    return () => {
      isMounted = false;
    };
  }, [localeValue, loadLocaleData]);

  return (
    <ReactIntlProvider
      {...rest}
      defaultLocale="en-US"
      locale={localeValue}
      messages={messages}
    >
      {children}
    </ReactIntlProvider>
  );
};
